import React, {useState, useEffect, useCallback, useMemo} from "react";
import { useParams, useNavigate } from "react-router-dom";
import Menu from "../../../../../components/layout/menu/index";
import AnswerQuestionSurvey from "../../../../../components/card/answerQuestionSurvey";
import SaveButton from "../../../../../components/forms/saveButton";
import CancelButton from "../../../../../components/forms/cancelButton";
import { getSurveyById, respondSurvey } from "../../../../../client/surveys";
import { useAuth } from "../../../../../context/auth";
import ModalSuccess from "../../../../../components/shared/modals/modalSuccess";
import ModalAlertWarning from "../../../../../components/shared/modals/modalAlertWarning";

export default function AnswerSurvey() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen); 

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  
  const [initialAnswers, setInitialAnswers] = useState({}); 

  
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalError, setModalError] = useState("");

  useEffect(() => {
    const fetchSurveyDetails = async () => {
      try {
        setLoading(true);
        const response = await getSurveyById(parseInt(surveyId));
        
        if (response.data && response.data.survey) {
          setSurvey(response.data.survey);
          setInitialAnswers({});
        } else {
          setError("Encuesta no encontrada");
        }
      } catch (err) {
        console.error("Error al cargar encuesta:", err);
        setError("Error al cargar la encuesta");
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchSurveyDetails();
    }
  }, [surveyId]);

 const handleAnswerChange = useCallback((questionId, answerData) => {
    setAnswers(prev => {
      const newAnswers = { ...prev };
      if (answerData) {
        newAnswers[questionId] = answerData;
      } else {
        delete newAnswers[questionId];
      }
      return newAnswers;
    });
  }, [setAnswers]);

  const hasChanges = () => {
    return JSON.stringify(answers) !== JSON.stringify(initialAnswers);
  };

  const resetAnswers = () => {
    setAnswers({...initialAnswers});
  };

const memoQuestions = useMemo(() => {
  return survey?.questions?.map(q => ({
    ...q,
  })) || [];
}, [survey?.questions]);

const handleSave = async () => {
  if (!user || !surveyId) {
    setModalError("Usuario no identificado");
    setShowErrorModal(true);
    return;
  }

  const answersArray = Object.values(answers);
   
  if (answersArray.length === 0) {
    setModalError("Debes responder al menos una pregunta");
    setShowErrorModal(true);
    return;
  }

  const mandatoryQuestions = survey.questions.filter(q => q.obligatory);
  const missingMandatory = mandatoryQuestions.filter(q => !answers[q.id]);
  
  if (missingMandatory.length > 0) {
    setModalError(`Faltan respuestas a preguntas obligatorias: ${missingMandatory.map(q => q.text).join(', ')}`);
    setShowErrorModal(true);
    return;
  }

  const emptyOtherAnswers = answersArray.filter(answer => {
    if (answer.otherText !== undefined && answer.otherText.trim() === "") {
      const question = survey.questions.find(q => q.id === answer.questionId);
      return question && question.hasOtherOption;
    }
    return false;
  });

  if (emptyOtherAnswers.length > 0) {
    setModalError("Por favor, completa el campo 'Otros' en las preguntas donde lo has seleccionado");
    setShowErrorModal(true);
    return;
  }

  try {
    setSaving(true);
    setSaveError(null);

    const responseData = {
      surveyId: parseInt(surveyId),
      userId: user.id || user.userId,
      answers: answersArray
    };


    const response = await respondSurvey(responseData);
    
    if (response.status === 200) {
      setInitialAnswers({...answers});
      setShowSuccessModal(true);
    } else {
      throw new Error(response.data?.message || "Error al guardar encuesta");
    }
  } catch (err) {
    console.error("Error al guardar encuesta:", err);
    const errorMessage = err.response?.data?.message || err.message || "Error al guardar la encuesta";
    setModalError(errorMessage);
    setShowErrorModal(true);
  } finally {
    setSaving(false);
  }
};

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/mis-encuestas");
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  const handleCancel = () => {
    if (hasChanges()) {
      resetAnswers();
    }
  };

  if (loading) {
    return (
      <div>
        <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
        <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
          <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-3">
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-[#6B7280] font-itcbook text-center text-xl">
                Cargando encuesta...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div>
        <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
        <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
          <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-3">
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-xl text-center text-red-500 font-itcbook">
                {error || "No se pudo cargar la encuesta"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
        <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
            <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-3">
                <h1 className="text-blue font-itcbold text-2xl md:text-[32px]">
                    Completa tu encuesta
                </h1>
                <h2 className="pt-2 text-2xl font-itcbook text-blue">
                    {survey.title}
                </h2>
                {survey.description && (
                  <h3 className="text-[#6B7280] font-itcbook text-xl">
                    {survey.description}
                  </h3>
                )}
<div className="flex flex-col gap-6 pt-4">
  {memoQuestions && memoQuestions.map((question, index) => (
    <AnswerQuestionSurvey 
      key={question.id}
      questionId={question.id}
      formatId={question.formatId}
      title={question.text}
      image={question.image}
      obligatory={question.obligatory}
      options={question.options || []}
      hasOtherOption={question.hasOtherOption}
      branches={question.branches}
      onAnswerChange={handleAnswerChange}
      resetFlag={Object.keys(answers).length === 0}
    />
  ))}
</div>

                <div className="flex items-center justify-center w-full gap-6 pt-10">
                  <SaveButton 
                      title="Guardar" 
                      onClick={handleSave}
                      disabled={saving || Object.keys(answers).length === 0}
                      loading={saving}
                  />
                  <CancelButton 
                    onClick={handleCancel}
                    disabled={!hasChanges()}
                  />
                </div>
            </div>
        </div>

        <ModalSuccess 
          showModalSuccess={showSuccessModal}
          closeModalSuccess={handleSuccessModalClose}
          fill="green"
          text="Encuesta guardada exitosamente"
        />

        <ModalAlertWarning 
          showModalAlertWarning={showErrorModal}
          closeModal={handleErrorModalClose}
          error={modalError}
          accept={handleErrorModalClose}
        />
    </div>
  );
}